import { lazy } from "react";
import { toast, Tooltip } from "top-brains-ui";
import { TreeData } from "@src/components/tree/TreeTypes";
import { ERROR_MSGS_CREATE_SYLLABUS } from "@src/constants/UiConstants";
import {
  parseISO,
  format,
  parse,
  isValid,
  differenceInDays,
  differenceInHours,
  setMilliseconds,
  setSeconds,
} from "date-fns";
import { cloneDeep } from "lodash";
import { formatInTimeZone, toDate, toZonedTime } from "date-fns-tz";
import { useEffect, useState } from "react";
import { Reward } from "@src/pages/student/tournaments/student-tournaments-list/types";
import { Level } from "@src/pages/super-admin/organization/OrganizationLayout";
import { StudentAttendaceData } from "@src/pages/batches/attendance/attendance.types";
import { REPORT_ANALYTICS_COLORS } from "@src/styles/ColorConstants";

type TooltipPosition =
  | "bottom"
  | "top"
  | "left"
  | "right"
  | "top-start"
  | "top-end"
  | "bottom-start"
  | "bottom-end";

export const formatDate = (dateString?: string | null): string => {
  if (!dateString) return "";
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const dateParts = dateString.split(" ");
  if (dateParts.length < 2) return "";

  const [year, month, day] = dateParts[0].split("-").map(Number);
  const [hours, minutes, seconds] = dateParts[1].split(":").map(Number);

  if (
    isNaN(year) ||
    isNaN(month) ||
    isNaN(day) ||
    isNaN(hours) ||
    isNaN(minutes) ||
    isNaN(seconds)
  ) {
    return "";
  }

  const date = new Date(year, month - 1, day, hours, minutes, seconds);

  const formattedDay = day.toString().padStart(2, "0");
  const formattedMonth = months[month - 1];
  const formattedYear = year;

  let hour = date.getHours();
  const isPM = hour >= 12;
  hour = hour % 12 || 12;
  const formattedTime = `${hour}:${minutes.toString().padStart(2, "0")} ${
    isPM ? "PM" : "AM"
  }`;

  return `${formattedDay} ${formattedMonth} ${formattedYear} ${formattedTime}`;
};

export const capitalizeFirstLetter = (text: string): string => {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const validateName = (value: string) => {
  if (!value) {
    return ERROR_MSGS_CREATE_SYLLABUS.NAME_REQUIRED;
  }
  if (value.length < 3) {
    return ERROR_MSGS_CREATE_SYLLABUS.NAME_MIN_ERR_MSG;
  }
  if (value.length > 50) {
    return ERROR_MSGS_CREATE_SYLLABUS.NAME_MAX_ERR_MSG;
  }
  if (!/^[a-zA-Z ]*$/.test(value)) {
    return ERROR_MSGS_CREATE_SYLLABUS.NAME_SPL_ERR_MSG;
  }
  return "";
};

export const dateFormat = (dateString: string): string => {
  if (!dateString) return "";

  let parsedDate: Date | null = null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    parsedDate = parseISO(dateString);
  } else if (
    /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2} GMT[+-]\d{1,2}:\d{2}$/.test(
      dateString,
    )
  ) {
    const cleanedDateString = dateString.replace(/ GMT[+-]\d{1,2}:\d{2}$/, "");
    parsedDate = parse(cleanedDateString, "dd/MM/yyyy HH:mm:ss", new Date());
  } else {
    parsedDate = new Date(dateString);
  }

  if (!parsedDate || !isValid(parsedDate)) {
    console.error("Invalid date format:", dateString);
    return "";
  }

  return format(parsedDate, "dd MMM yyyy hh:mm a");
};

export const handleSelectNode = (id: string, nodes: TreeData[], visited = new Set<any>()) => {
  let didChange = false;

  const updateNodes = (treeNodes: TreeData[]): TreeData[] => {
    let branchChanged = false;

    const updatedNodes = treeNodes.map((node) => {
      if (!node || visited.has(node)) return node;
      visited.add(node);
      const nextSelected = node._id === id ? !node.selected : false;
      const nextChildren = node.children
        ? updateNodes(node.children)
        : undefined;
      const nodeChanged =
        node.selected !== nextSelected || nextChildren !== node.children;

      if (!nodeChanged) {
        return node;
      }

      branchChanged = true;
      return {
        ...node,
        selected: nextSelected,
        children: nextChildren,
      };
    });

    if (branchChanged) {
      didChange = true;
      return updatedNodes;
    }

    return treeNodes;
  };

  const updatedTree = updateNodes(nodes);
  return didChange ? updatedTree : nodes;
};
export function handleCheckNode(
  nodes: TreeData[],
  targetId: string,
  isSelected?: boolean,
) {
  const newNodes = cloneDeep(nodes);
  const nodeMap = new Map<string, TreeData>();

  // Recursively add all nodes (chapters, topics, and subtopics) into a flat map
  const populateNodeMap = (items: TreeData[], visited = new Set<string>()) => {
    items.forEach((item) => {
      if (!item || visited.has(item._id)) return;
      visited.add(item._id);
      nodeMap.set(item._id, item);
      if (item.children) {
        populateNodeMap(item.children, visited);
      }
    });
  };

  populateNodeMap(newNodes); // Deep copy all nodes and children

  // Recursive function to update selection state
  function selectNode(nodeId: string, select?: boolean, visited = new Set<string>()) {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);

    const node = nodeMap.get(nodeId);
    if (!node) return;

    node.selected = select;
    node.partial = false;

    // If the node has children, update them recursively
    if (node.hasChildren && node.children) {
      node.children.forEach((child) => selectNode(child._id, select, visited));
    }
  }

  // Recursive function to update parent selection state
  function updateParentState(nodeId: string, visited = new Set<string>()) {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);

    const node = nodeMap.get(nodeId);
    if (!node || !node.parentId) return;

    const parent = nodeMap.get(node.parentId);
    if (!parent) return;

    if (parent._id === nodeId) return;

    const selectedCount = parent.children?.filter(
      (child) => child.selected,
    ).length;
    const partialSelectedCount = parent.children?.filter(
      (child) => child.partial,
    ).length;
    const totalChildren = parent.children?.length;

    if (partialSelectedCount) {
      parent.selected = false;
      parent.partial = true;
    } else if (selectedCount === 0) {
      parent.selected = false;
      parent.partial = false;
    } else if (selectedCount === totalChildren) {
      parent.selected = true;
      parent.partial = false;
    } else {
      parent.selected = false;
      parent.partial = true;
    }

    // Recursively update grandparent
    updateParentState(parent._id, visited);
  }

  // Step 1: Select or deselect the target node
  selectNode(targetId, isSelected);

  // Step 2: Update parent state
  const targetNode = nodeMap.get(targetId);
  if (targetNode) updateParentState(targetNode._id);

  return newNodes;
}

export const convertTo24Hour = (time12h: string): string => {
  const [time, modifier] = time12h.split(" ");
  if (!time || !modifier) return "00:00";

  // eslint-disable-next-line prefer-const
  let [hours, minutes] = time.split(":");
  if (!hours || !minutes) return "00:00";

  if (hours === "12") {
    hours = "00";
  }

  if (modifier.toUpperCase() === "PM") {
    hours = (parseInt(hours, 10) + 12).toString();
  }

  return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
};

export const convertTo12Hour = (time24: string): string => {
  const [hoursStr, minutesStr] = time24.split(":");
  const hours = parseInt(hoursStr ?? "0", 10);
  const minutes = parseInt(minutesStr ?? "0", 10);

  const ampm = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 || 12;

  return `${hours12.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")} ${ampm}`;
};

export const isValid12HourTime = (timeString: string): boolean => {
  const regex = /^(0[1-9]|1[0-2]):([0-5][0-9])(?:\s?(AM|PM))?$/i;
  return regex.test(timeString);
};

export const truncateText = (
  value: string,
  maxLength: number = 25,
  position: TooltipPosition = "bottom",
): JSX.Element | null => {
  if (!value) return null;

  const shouldTruncate = value.length > maxLength;
  return shouldTruncate ? (
    <Tooltip title={value} placement={position}>
      <span>{value.substring(0, maxLength)}...</span>
    </Tooltip>
  ) : (
    <span>{value}</span>
  );
};

/**
 * This util can be used to make hasChildren key false
 * @param data : treeArray
 * @param type : type = SUBJECT | CHAPTER | TOPIC | SUBTOPIC
 */
export function updateHasChildrenRecursive(data: TreeData[], type: string, visited = new Set<any>()) {
  data.forEach((item) => {
    if (!item || visited.has(item)) return;
    visited.add(item);
    if (Array.isArray(item.children)) {
      item.children.forEach((child) => {
        if (child.type === type) {
          child.hasChildren = false;
        }
      });

      updateHasChildrenRecursive(item.children, type, visited);
    }
  });
  return data;
}

/**
 * This function is used to find the node in the tree with node id.
 * @param id  string|null, id of node which we want to find in tree.
 * @param nodes TreeData[], Array of tree from where we want to find the node.
 * @return  TreeData|null, node at that id.
 */
export const findNodeById = (
  id: string | null,
  nodes: TreeData[],
  visited = new Set<any>(),
): TreeData | null => {
  for (const node of nodes) {
    if (!node || visited.has(node)) continue;
    visited.add(node);
    if (node._id === id) return node;
    let childNode = null;
    if (node.children) childNode = findNodeById(id, node.children, visited);
    if (childNode) return childNode;
  }
  return null;
};

type valueType = any;

export const checkEmpty = (value: valueType): boolean => {
  // Check for null or undefined
  if (value == null) return true;

  // Check for strings
  if (typeof value === "string") return value.trim().length === 0;

  // Check for arrays
  if (Array.isArray(value)) return value.length === 0;

  // Check for objects
  if (typeof value === "object") {
    // Handle Map and Set
    if (value instanceof Map || value instanceof Set) return value.size === 0;

    // Handle regular objects
    return Object.keys(value).length === 0;
  }

  // For all other types (numbers, booleans, etc.), return false as they are not "empty"
  return false;
};

/** This will create a syllabus tree from selected nodes */
export function filterSelectedNodes(tree: TreeData[], visited = new Set<any>()) {
  return tree.reduce((acc: TreeData[], node) => {
    if (!node || visited.has(node)) return acc;
    visited.add(node);
    let filteredChildren: TreeData[] = [];
    if (node.children && node.children.length > 0) {
      filteredChildren = filterSelectedNodes(node.children, visited);
    }

    // Include this node if:
    // - It is selected or partial
    // - Or any of its children are selected/partial
    if (node.selected || node.partial || filteredChildren.length > 0) {
      acc.push({
        ...node,
        children: filteredChildren,
      });
    }
    return acc;
  }, []);
}

/**
 * Format a Date object to 'yyyy-MM-dd HH:mm:ss' in the specified time zone.
 */
export const formatDateToISTString = (
  date: Date = new Date(),
  timeZone: string = "Asia/Kolkata",
  format: string = "yyyy-MM-dd HH:mm:ss",
): string => {
  return formatInTimeZone(date, timeZone, format);
};

/**  * Parses a date string like "2025-04-25 15:22:00 IST" to a JS Date object in UTC. */
export const parseISTDateString = (
  dateStr: string = "",
  timeZone: string = "Asia/Kolkata",
): Date => {
  const cleanStr = dateStr?.replace?.(/\s[A-Z]{2,4}$/, "") ?? "";
  const inputFormat = "yyyy-MM-dd HH:mm:ss";

  const parsed = parse(cleanStr, inputFormat, new Date());
  const date = toDate(parsed, { timeZone });

  return date;
};

//!Function To Copy Text

export const copyToClipboard = (text: string) => {
  navigator?.clipboard?.writeText(text)?.then(
    () => {
      toast.success("Success", "Text Copied");
    },
    () => {
      toast.error("Error", "Could not copy text");
    },
  );
};

export const formatPrivilege = (privilege: string) => {
  if (!privilege) return "";
  return privilege
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const formatDuration = (duration: number | undefined | null): string => {
  if (typeof duration !== "number") return "--";
  if (duration < 60) return `${duration} Min${duration === 1 ? "" : "s"}`;
  const hours = Math.floor(duration / 60);
  return `${hours} Hr${hours === 1 ? "" : "s"}`;
};

export const formatStartedAtDate = (dateString: string | undefined) => {
  const date = new Date(parseISTDateString(dateString));
  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear().toString().slice(-2);
  return `${day}-${month}-${year}`;
};

/** This function will calculate past duration from current time */
export const calculatePastTime = (dateString: string | undefined) => {
  const pastDate = parseISTDateString(dateString);
  const now = new Date();

  const days = differenceInDays(now, pastDate);
  const hours = differenceInHours(now, pastDate) % 24;

  const formatted = `${days} days, ${hours} hours ago`;
  return formatted;
};

//Function for fullscreen
export const useFullscreenLock = () => {
  const [showModal, setShowModal] = useState(true);
  const [isInitial, setIsInitial] = useState(true);

  const enterFullscreen = async () => {
    try {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        await elem.requestFullscreen();
      } else if ((elem as any).webkitRequestFullscreen) {
        await (elem as any).webkitRequestFullscreen();
      } else if ((elem as any).msRequestFullscreen) {
        await (elem as any).msRequestFullscreen();
      }
    } catch (err) {
      console.error("Failed to enter fullscreen:", err);
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      const isFullscreen =
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).msFullscreenElement;

      if (!isFullscreen) {
        setIsInitial(false);
        setShowModal(true);
      }
    };

    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);
    document.addEventListener("msfullscreenchange", onFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        onFullscreenChange,
      );
      document.removeEventListener("msfullscreenchange", onFullscreenChange);
    };
  }, []);

  const handleReEnter = async () => {
    await enterFullscreen();
    setShowModal(false);
  };

  return { showModal, isInitial, handleReEnter };
};

/**  Example input: "2025-05-02 16:57:45 IST"
 *   Return: UTC Date object
 */
export function convertISTStringToUTC(dateTimeStr: string): Date {
  const istTimeCleaned = dateTimeStr
    .replace("IST", "")
    .trim()
    .replace(" ", "T");

  // Timezone string format required by date-fns-tz
  const timeZone = "Asia/Kolkata";

  // Parse and convert IST to UTC
  return toZonedTime(istTimeCleaned, timeZone);
}

/** Input: Date object
 * Return : "20250502T165745Z" Date string
 */
export const formatDateUTC = (date: Date) => {
  return format(date, "yyyyMMdd'T'HHmmss'Z'");
};

export const truncateMultiSelectText = (
  text: string,
  maxLength: number,
): string => {
  if (!text || maxLength <= 0) return "";
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

export const isFrontendLanguage = (language?: string): boolean => {
  if (!language) return false;
  const frontendLanguages = ["html", "css", "javascript"];
  return frontendLanguages.includes(language.toLowerCase());
};

export function parseInputToStringArray(input: string): string[] {
  if (!input || typeof input !== "string") return [];

  const result: string[] = [];
  let current = "";
  let depth = 0;
  let inQuotes = false;
  let escapeNext = false;
  const pushCurrent = () => {
    if (current.trim()) {
      result.push(current.trim());
    }
    current = "";
  };

  for (let index = 0; index < input.length; index += 1) {
    const char = input[index];
    if (escapeNext) {
      current += char;
      escapeNext = false;
      continue;
    }

    if (char === "\\" && input[index + 1] === "n" && depth === 0 && !inQuotes) {
      pushCurrent();
      index += 1;
      continue;
    }

    if (char === "\\") {
      escapeNext = true;
      current += char;
      continue;
    }

    if (char === '"') {
      inQuotes = !inQuotes;
      current += char;
      continue;
    }

    if (!inQuotes) {
      if (char === "[" || char === "{" || char === "(") depth++;
      if (char === "]" || char === "}" || char === ")") depth--;
    }

    if (
      (char === "," || char === "\n" || char === "\r") &&
      depth === 0 &&
      !inQuotes
    ) {
      pushCurrent();

      if (char === "\r" && input[index + 1] === "\n") {
        index += 1;
      }
    } else {
      current += char;
    }
  }

  pushCurrent();

  return result;
}

/**
 *
 * @param date
 * @returns The new date with seconds and milliseconds set to 0
 */
export const startOfMinuteExact = (date: Date) =>
  setMilliseconds(setSeconds(date, 0), 0);

export const percentageCalculator = (count: number, limit: number) => {
  return (count / limit) * 100;
};

interface AssessmentData {
  [key: string]: any;
  quizSectionList?: Array<{ sectionName: string }>;
  additionDetails?: Record<string, any>;
  additionalDetails?: Record<string, any>;
}

interface StudentData {
  [key: string]: any;
  organization?: string;
  subOrganization?: string;
  branch?: string;
  totalScore?: number;
  totalTime?: number;
  additionalDetails?: Record<string, any>;
  studentSectionDataList?: Array<{ sectionName: string }>;
}

interface ResultItem {
  label: string;
  values: string[];
}

export function processDownloadSelectionKeys(
  assessmentData: AssessmentData,
  type: "quiz" | "tournament",
  studentData: StudentData,
): ResultItem[] {
  const result: ResultItem[] = [];

  // 1. Student Details
  const studentBlocklist: string[] = [
    "id",
    "studentId",
    "quizId",
    "additionalDetails",
    "studentSectionDataList",
    "name",
    "studentName",
    "email",
    "studentEmail",
    "tournamentId",
    "studentQuestionMetaDataList",
    "reward",
    "rank",
    "userEmail",
  ];

  const blockTournamentList = [
    "totalTime",
    "totalQuestions",
    "totalScore",
    "malPractice",
    "questionsAttended",
  ];

  const finalBlocklist =
    type === "tournament"
      ? [...studentBlocklist, ...blockTournamentList]
      : studentBlocklist;

  const studentDetailsKeys = Object.keys(studentData).filter(
    (key) => !finalBlocklist.includes(key),
  );

  result.push({
    label: "studentDetails",
    values: [...new Set(studentDetailsKeys)],
  });

  if (type === "quiz") {
    const sectionNames =
      assessmentData.quizSectionList?.map(
        (section) => `Section ${section.sectionName}`,
      ) || [];

    result.push({
      label: "questionDetails",
      values: sectionNames,
    });
  } else {
    const tournamentDetails = [
      "totalScore",
      "totalTime",
      "totalQuestions",
      "questionsAttended",
      "passedQuestions",
      "malPractice",
    ];

    result.push({
      label: "tournamentDetails",
      values: tournamentDetails,
    });
  }

  // 3. Additional Details
  const additionalDetails =
    studentData.additionalDetails ||
    assessmentData.additionDetails ||
    assessmentData.additionalDetails;

  if (additionalDetails) {
    const additionalBlocklist = [""];
    const additionalKeys = Object.keys(additionalDetails).filter(
      (key) => !additionalBlocklist.includes(key),
    );

    if (additionalKeys.length > 0) {
      result.push({
        label: "additionalDetails",
        values: additionalKeys,
      });
    }
  }

  return result;
}

export function formatCamelCasePreserveCapitals(input: string): string {
  if (/\s|[-_]/.test(input)) {
    if (input.startsWith("section-")) {
      const sectionName = input.slice(8);
      return `Section ${formatCamelCasePreserveCapitals(sectionName)}`;
    }
    return input;
  }
  const result = input
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .replace(/^./, (firstChar) => firstChar.toUpperCase());

  return result;
}

export const getRewardText = (reward: Reward) => {
  if (reward?.startPosition === reward?.endPosition) {
    return `${reward.startPosition}${
      reward?.startPosition === 1
        ? "st"
        : reward?.startPosition === 2
          ? "nd"
          : reward?.startPosition === 3
            ? "rd"
            : "th"
    }`;
  }
  return `${reward?.startPosition}-${reward?.endPosition}`;
};

/**
 *
 * @param obj
 * @returns first available alphabet for obj as a key which is not in obj
 */
export function getNextAlphabetKey(obj: Record<string, any>): string | null {
  const alphabets = "abcdefghijklmnopqrstuvwxyz".split("");

  for (let i = 0; i < alphabets.length; i++) {
    if (!(alphabets[i] in obj)) {
      return alphabets[i];
    }
  }
  return null;
}

/**
 *
 * @param base64
 * @returns image src prefixed with data string and base64
 */
export function getImageSrc(base64: string): string {
  if (!base64) return "";

  if (base64.startsWith("data:")) return base64;

  // Check raw base64 prefixes
  if (base64.startsWith("iVBORw0KGgo")) {
    return `data:image/png;base64,${base64}`;
  }
  if (base64.startsWith("/9j/")) {
    return `data:image/jpeg;base64,${base64}`;
  }
  if (base64.startsWith("R0lGOD")) {
    return `data:image/gif;base64,${base64}`;
  }
  if (base64.startsWith("UklGR")) {
    return `data:image/webp;base64,${base64}`;
  }

  // Decode a small chunk to check for <svg
  try {
    const decoded = atob(base64.slice(0, 100));
    if (decoded.includes("<svg")) {
      return `data:image/svg+xml;base64,${base64}`;
    }
  } catch (e) {
    console.warn("Invalid base64 image", e);
  }

  // Default fallback
  return `data:image/png;base64,${base64}`;
}

export const formatText = (text: string): string => {
  return text
    ?.toLowerCase()
    .split("_")
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const handleElpSelectNode = (
  nodeId: string,
  treeData: TreeData[],
): TreeData[] => {
  const updateNodeSelection = (nodes: TreeData[]): TreeData[] => {
    return nodes.map((node) => {
      if (node._id === nodeId) {
        return {
          ...node,
          selected: true,
        };
      }

      if (node.children) {
        return {
          ...node,
          children: updateNodeSelection(node.children),
          selected: node.children.some((child) => child.selected),
        };
      }

      return {
        ...node,
        selected: false,
      };
    });
  };

  return updateNodeSelection(treeData);
};

export const elpFindNodeById = (
  id: string,
  nodes: TreeData[],
): TreeData | null => {
  for (const node of nodes) {
    if (node._id === id) return node;
    if (node.children) {
      const found = elpFindNodeById(id, node.children);
      if (found) return found;
    }
  }
  return null;
};

export const collapseAllNodes = (nodes: TreeData[]): TreeData[] => {
  return nodes.map((node) => ({
    ...node,
    expanded: false, // reset expanded state
    children: node.children ? collapseAllNodes(node.children) : [],
  }));
};

export async function validateTime() {
  try {
    const BASE_URL: string = import.meta.env.VITE_TEST_FRESHERS_BASE_URL;
    const response = await fetch(`${BASE_URL}common/get-current-time`);
    const data = await response.json();

    const actualIST = parseISTDateString(data.response);
    const systemTime = new Date();

    // Calculate difference in minutes
    const diffMs = Math.abs(actualIST?.getTime() - systemTime.getTime());
    const diffMinutes = diffMs / (1000 * 60);

    // Allow only if within 2 minutes
    if (diffMinutes <= 2) {
      return {
        state: true,
        message: "",
      };
    } else {
      return {
        state: false,
        message: "Your system time is incorrect. Please sync to IST.",
      };
    }
  } catch (err) {
    console.error("Error fetching time:", err);
    return { state: false, message: "Network issue, please try again." };
  }
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window !== "undefined") {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const media = window.matchMedia(query);

    const listener = () => setMatches(media.matches);

    media.addEventListener("change", listener);
    setMatches(media.matches);

    return () => {
      media.removeEventListener("change", listener);
    };
  }, [query]);

  return matches;
}

export const getPageSize = () => {
  const container = document.getElementById("tf-table-container");
  if (!container) return 20;

  const height = container.getBoundingClientRect().height;
  const rows = Math.floor(height / 32);

  return rows < 10 ? 20 : rows;
};

export const formatSecondsToMinutes = (totalSeconds?: number) => {
  if (!totalSeconds && totalSeconds !== 0) return "0m 0s";

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}m ${seconds}s`;
};

export const hslToHex = (h: number, s: number, l: number): string => {
  s /= 100;
  l /= 100;

  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) =>
    l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

  return (
    "#" +
    [f(0), f(8), f(4)]
      .map((x) =>
        Math.round(x * 255)
          .toString(16)
          .padStart(2, "0"),
      )
      .join("")
  );
};

export const generateUniqueHexColors = (count: number): string[] => {
  const colors: string[] = [];

  for (let i = 0; i < count; i++) {
    const hue = Math.floor((360 / count) * i);
    const saturation = 70;
    const lightness = 50;

    colors.push(hslToHex(hue, saturation, lightness));
  }

  return colors;
};

export const convertMinutesToHr = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (mins === 0) {
    return `${hrs} hr`;
  }

  return `${hrs} hr ${mins} min`;
};

export type LevelInput =
  | "ORGANIZATION"
  | "SUB_ORGANIZATION"
  | "CLIENT"
  | "BRANCH"
  | "BATCH";

export type LevelOutput = "ORG" | "SUB_ORG" | "CLIENT" | "BRANCH" | "BATCH";

export function mapLevel(level: LevelInput): LevelOutput {
  const levelMap: Record<LevelInput, LevelOutput> = {
    ORGANIZATION: "ORG",
    SUB_ORGANIZATION: "SUB_ORG",
    CLIENT: "CLIENT",
    BRANCH: "BRANCH",
    BATCH: "BATCH",
  };

  return levelMap[level];
}

const levelLabelMap: Record<Exclude<Level, null>, string> = {
  ORG: "Organization",
  SUB_ORG: "Sub Organization",
  CLIENT: "Client",
  BRANCH: "Branch",
  BATCH: "Batch",
};

export const getLevelLabel = (level: Level): string => {
  if (!level) return "";

  return levelLabelMap[level];
};

/**
 * @use This is used to get status and percentage of students attendance for single batch
 * @param data
 * @returns object with status count and percentage of status
 */
type statusLabel = {
  excellent: number;
  good: number;
  defaulter: number;
  at_risk: number;
};
export const getStatusStats = (data: StudentAttendaceData[]) => {
  return data.reduce(
    (acc, item) => {
      const status = item.status.toLowerCase() as keyof statusLabel;

      if (!acc[status]) {
        acc[status] = 0;
      }

      acc[status] += 1;

      return acc;
    },
    {
      excellent: 0,
      good: 0,
      defaulter: 0,
      at_risk: 0,
    },
  );
};

export const getColorByPercentage = (percentage: number) => {
  if (percentage === 100) return REPORT_ANALYTICS_COLORS.GREEN;
  if (percentage > 80) return REPORT_ANALYTICS_COLORS.PRIMARY;
  if (percentage > 60) return REPORT_ANALYTICS_COLORS.SECONDARY;
  return REPORT_ANALYTICS_COLORS.TERTIARY;
};

export const getWeekInfo = (dateStr: string) => {
  const d = new Date(dateStr);

  const day = d.getDay(); // 0 = Sun, 1 = Mon
  const diff = day === 0 ? -6 : 1 - day;

  const start = new Date(d);
  start.setDate(d.getDate() + diff); // Monday

  const end = new Date(start);
  end.setDate(start.getDate() + 6); // Sunday

  return {
    key: start.toISOString().slice(0, 10),
    label: `Week (${start.toDateString()} - ${end.toDateString()})`,
  };
};

export const formatRangeDate = (date: Date) => {
  return date.toISOString().split("T")[0];
};

export const formatDateYYMMDD = (input?: Date | string | null) => {
  if (!input) return "";
  const date = input instanceof Date ? input : new Date(input);
  if (isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};
export const getDateRange = (range: number) => {
  const today = new Date();
  const past = new Date();
  past.setDate(today.getDate() - range);

  return {
    startDate: formatRangeDate(past),
    endDate: formatRangeDate(today),
  };
};

export const getMonthRange = (filters: string[]) => {
  const today = new Date();

  if (!filters || filters.length === 0) {
    filters = ["current"];
  }

  const hasCurrent = filters.includes("current");
  const hasLast = filters.includes("last");
  const has2Months = filters.includes("2months");
  const has3Months = filters.includes("3months");

  let key = "";

  if (has3Months) key = "3months";
  else if (has2Months) key = "2months";
  else if (hasCurrent && hasLast) key = "current+last";
  else if (hasLast) key = "last";
  else key = "current";

  let from: Date;
  let to: Date;

  switch (key) {
    case "3months":
      from = new Date(
        today.getFullYear(),
        today.getMonth() - 3,
        today.getDate(),
      );
      to = new Date(today);
      break;

    case "2months":
      from = new Date(
        today.getFullYear(),
        today.getMonth() - 2,
        today.getDate(),
      );
      to = new Date(today);
      break;

    case "current+last":
      from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      break;

    case "last":
      from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      to = new Date(today.getFullYear(), today.getMonth(), 0);
      break;

    case "current":
    default:
      from = new Date(today.getFullYear(), today.getMonth(), 1);
      to = new Date(today);
      break;
  }

  return {
    fromDate: formatDateYYMMDD(from),
    endDate: formatDateYYMMDD(to),
  };
};

export const readExcelFile = async (file: File): Promise<string[]> => {
  const XLSX = await import("xlsx");

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const arrayBuffer = event.target?.result;
      if (!arrayBuffer) {
        reject("No file data");
        return;
      }

      const data = new Uint8Array(arrayBuffer as ArrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // 👇 Get first row as header (2D array format)
      const sheetData: unknown[][] = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: "",
      });

      if (!sheetData || sheetData.length === 0) {
        reject("Excel file is empty");
        return;
      }

      const firstRow = sheetData[0];

      const firstCell = firstRow?.[0];

      if (
        !firstRow ||
        typeof firstCell !== "string" ||
        firstCell.trim().toLowerCase() !== "email id"
      ) {
        reject("Invalid file format. First column must be 'Email ID'");
        return;
      }

      // 👇 Now safely convert to JSON
      const jsonData = XLSX.utils.sheet_to_json<Record<string, unknown>>(
        worksheet,
        {
          defval: "",
          raw: false,
        },
      );

      const emails = jsonData
        .map((item) => item["Email ID"]?.toString().trim())
        .filter(Boolean);

      resolve(emails as string[]);
    };

    reader.onerror = () => reject("Error reading file");
    reader.readAsArrayBuffer(file);
  });
};

/**
 * This function takes base64 encoded Excel data and triggers a download of the file with the specified name.
 * @param base64Data
 * @param fileName
 */
export const downloadBase64Excel = (
  base64Data: string,
  fileName: string = "file.xlsx",
) => {
  try {
    // Remove metadata if present (e.g. data:application/vnd...;base64,)
    const base64 = base64Data.includes(",")
      ? base64Data.split(",")[1]
      : base64Data;

    // Convert base64 → binary string
    const binaryString = window.atob(base64);

    // Convert binary string → Uint8Array
    const len = binaryString.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Create blob for Excel
    const blob = new Blob([bytes], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Create download link
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;

    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error("Error downloading Excel file:", error);
  }
};

/**
 * Triggers a download of a Blob with the specified filename.
 * @param blob
 * @param fileName
 */
export const downloadBlob = (blob: Blob, fileName: string) => {
  try {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error("Error downloading blob:", error);
  }
};



const ChunkLoadFailed = () => (
  <div
    style={{
      minHeight: "60vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 12,
      padding: 24,
      textAlign: "center",
    }}
  >
    <p style={{ margin: 0, fontWeight: 600 }}>This page failed to load.</p>
    <p style={{ margin: 0, maxWidth: 420 }}>
      Your connection may be slow or the page script did not finish downloading.
      Reload to try again.
    </p>
    <button type="button" onClick={() => window.location.reload()}>
      Reload page
    </button>
  </div>
);

export const lazyWithRetry = (componentImport: () => Promise<any>) =>
  lazy(async () => {
    const lastRefreshedPath = window.sessionStorage.getItem("lazy-retry-path");
    const currentPath = window.location.pathname;

    try {
      const component = await componentImport();
      if (lastRefreshedPath === currentPath) {
        window.sessionStorage.removeItem("lazy-retry-path");
      }
      return component;
    } catch (err) {
      console.error("[lazyWithRetry] Caught error during componentImport for path " + currentPath + ":", err);
      if (lastRefreshedPath !== currentPath) {
        window.sessionStorage.setItem("lazy-retry-path", currentPath);
        window.location.reload();
        return new Promise(() => {});
      }
      return { default: ChunkLoadFailed };
    }
  });
